import { Fragment, useEffect, useState } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Pencil } from 'lucide-react'
import type { Holding, StrategyView } from '@/lib/api-types'
import { formatDuration, formatMarketCapRange, formatPrice, formatTokenAmountShort, formatUsd, formatUsdShort } from '@/lib/format'
import { useTRPC } from '@/lib/trpc'
import { tokenLogo } from '@/lib/default-tokens'
import { CUSTODY_LABELS } from '@/lib/pool'
import { TokenIcon } from './holdings-table'
import { CustodyLogo } from './custody-logo'
import { DuplicateButton, PauseResumeButton, StatusBadge, strategyKind } from './strategy-card'
import { MarketCapDialog } from './market-cap-dialog'
import { PIN, useScrolledSideways } from './pinned-column'
import type { Chain } from '../../../src/constants/chains'

const COLUMNS = 7

/** a strategy that has run its course: it sits under the live ones, folded away by default on the home page */
export function isSettledStrategy(s: StrategyView) {
  return s.status === 'completed' || s.status === 'cancelled'
}

/**
 * The strategies as rows (John, Nov 2 2026: the cards were hard to scroll and hid what he looks for). One line per
 * strategy with the token, where it trades, its state, the orders filled as a bar, what went out and came in, the
 * market cap gate with the token's market cap now, the next order, and pause or resume at the end of the row. The
 * row opens the detail view; the first column stays put while the rest scrolls sideways on a phone.
 */
export function StrategiesTable({
  strategies,
  holdings,
  completedOpen,
  onSelect,
  onDuplicate,
}: {
  strategies: StrategyView[]
  /** the page's holdings rows: a held token's market cap is read off its row, so both tables print one figure */
  holdings?: Holding[]
  /** whether the completed and stopped rows start unfolded */
  completedOpen: boolean
  onSelect: (id: string) => void
  /** a finished row's action (John, Nov 3 2026): the wizard opens filled in with the strategy */
  onDuplicate?: (strategy: StrategyView) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const sideways = useScrolledSideways()
  const [editing, setEditing] = useState<StrategyView | null>(null)
  // a saved range shows on the row at once
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.strategy.list.queryKey() })
    if (editing) void queryClient.invalidateQueries({ queryKey: trpc.strategy.get.queryKey({ id: editing.id }) })
  }
  const live = strategies.filter((s) => !isSettledStrategy(s))
  const settled = strategies.filter(isSettledStrategy)
  // nothing live: the settled rows are the table, so they show
  const [open, setOpen] = useState(completedOpen || live.length === 0)
  useEffect(() => {
    if (live.length === 0) setOpen(true)
  }, [live.length])
  // the market cap of each token now, beside the gate it must pass: a held token's is the holdings row's own figure (one
  // payload, one number on the page; John, Nov 3 2026: the two tables said $2.88M and $3.05M for the same token), the
  // rest ask for it, one query per distinct token (most strategies share one) at the holdings poll's pace
  const heldCaps = new Map<string, number>((holdings ?? []).flatMap((h) => (h.marketCapUsd !== undefined ? [[`${h.chain}:${h.address}`, h.marketCapUsd]] : [])))
  const tokens = [...new Map(strategies.map((s) => [`${s.chain}:${s.token.address}`, { chain: s.chain as Chain, address: s.token.address }])).values()]
  const markets = useQueries({
    queries: tokens.map((t) => ({ ...trpc.token.get.queryOptions({ chain: t.chain, address: t.address }), staleTime: 15_000, refetchInterval: 15_000, enabled: !heldCaps.has(`${t.chain}:${t.address}`) })),
  })
  const marketCapNow = new Map(tokens.map((t, i) => {
    const key = `${t.chain}:${t.address}`
    return [key, heldCaps.get(key) ?? markets[i]?.data?.marketCapUsd]
  }))
  const headers = (
    <tr>
      <th className={`${PIN} ${sideways.edge} w-max px-3 py-2 font-medium`}>Strategy</th>
      <th className="px-2.5 py-2 font-medium">Status</th>
      <th className="px-2.5 py-2 font-medium">Orders</th>
      <th className="px-2.5 py-2 text-right font-medium">Filled</th>
      <th className="px-2.5 py-2 text-right font-medium">Received</th>
      <th className="px-2.5 py-2 text-right font-medium">Per order</th>
      <th className="px-2.5 py-2 text-right font-medium">Market cap</th>
    </tr>
  )
  const row = (s: StrategyView) => (
    <StrategyRow
      key={s.id}
      strategy={s}
      edge={sideways.edge}
      marketCapNow={marketCapNow.get(`${s.chain}:${s.token.address}`)}
      onSelect={() => onSelect(s.id)}
      onEditMarketCap={() => setEditing(s)}
      onDuplicate={onDuplicate ? () => onDuplicate(s) : undefined}
    />
  )
  return (
    <>
      {/* sideways only: the page scrolls down (see pinned-column.ts); a sideways overscroll stays here */}
      <div onScroll={sideways.onScroll} className="overflow-x-auto overscroll-x-contain">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-muted">{headers}</thead>
          <tbody>
            {live.map(row)}
            {settled.length > 0 ? (
              <Fragment>
                {/* the fold between live and finished rows; its label stays put while the table scrolls sideways */}
                <tr className="border-t border-line">
                  <td colSpan={COLUMNS} className="p-0">
                    <button
                      type="button"
                      className="sticky left-0 inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-muted hover:text-text"
                      aria-expanded={open}
                      onClick={() => setOpen((v) => !v)}
                    >
                      <ChevronDown className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
                      {settled.length} {settled.length === 1 ? 'strategy' : 'strategies'} completed or stopped
                    </button>
                  </td>
                </tr>
                {open ? settled.map(row) : null}
              </Fragment>
            ) : null}
          </tbody>
        </table>
      </div>
      {editing ? <MarketCapDialog strategy={editing} open onClose={() => setEditing(null)} onSaved={refresh} /> : null}
    </>
  )
}

/** a raw amount in its short form: 150k HELL, 2.5 SOL */
function shortRaw(raw: string | bigint, decimals: number, symbol: string) {
  return formatTokenAmountShort(Number(BigInt(raw)) / 10 ** decimals, symbol)
}

/** how much of the plan has gone into orders (a failed order's amount comes back) */
function planDone(s: StrategyView) {
  const total = BigInt(s.dca.totalAmountRaw)
  if (total <= 0n) return 0n
  const remaining = BigInt(s.remainingAmountRaw)
  return remaining <= 0n ? total : remaining >= total ? 0n : total - remaining
}

/** the next order's time: the clock alone today, the date before it otherwise */
function formatNextRun(iso: string) {
  const at = new Date(iso)
  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  if (at.toDateString() === new Date().toDateString()) return time
  return `${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
}

/** a cell of two lines: the figure, and a muted line of context under it */
function Stacked({ top, bottom, align = 'right', tone = 'text-muted' }: { top: React.ReactNode; bottom?: React.ReactNode; align?: 'left' | 'right'; tone?: string }) {
  return (
    <div className={`whitespace-nowrap tabular-nums ${align === 'right' ? 'text-right' : ''}`}>
      <div>{top}</div>
      {bottom ? <div className={`mt-0.5 text-xs ${tone}`}>{bottom}</div> : null}
    </div>
  )
}

function StrategyRow({
  strategy: s,
  edge,
  marketCapNow,
  onSelect,
  onEditMarketCap,
  onDuplicate,
}: {
  strategy: StrategyView
  edge: string
  marketCapNow: number | undefined
  onSelect: () => void
  onEditMarketCap: () => void
  onDuplicate?: () => void
}) {
  const tokenSymbol = (s.token.symbol ?? 'token').toUpperCase()
  const settlementSymbol = s.settlement.symbol.toUpperCase()
  const inToken = s.side === 'sell' ? { symbol: tokenSymbol, decimals: s.token.decimals } : { symbol: settlementSymbol, decimals: s.settlement.decimals }
  const outToken =
    s.side === 'sell'
      ? { symbol: settlementSymbol, decimals: s.settlement.decimals, imageUrl: tokenLogo(s.settlement.address) }
      : { symbol: tokenSymbol, decimals: s.token.decimals, imageUrl: tokenLogo(s.token.address, s.token.imageUrl ?? undefined) }
  const limit = s.kind === 'limit'
  const orders = Math.max(1, s.dca.orders)
  const confirmed = Math.min(orders, s.confirmedOrders)
  const pending = Math.min(orders - confirmed, s.pendingOrders)
  const done = planDone(s)
  const received = s.receivedAmountRaw === null ? null : BigInt(s.receivedAmountRaw)
  // the market cap gate, and whether the token sits inside it now
  const { minMarketCapUsd: minCap, maxMarketCapUsd: maxCap } = s.limits
  const gated = minCap !== null || maxCap !== null
  const inside = marketCapNow !== undefined && (minCap === null || marketCapNow >= minCap) && (maxCap === null || marketCapNow <= maxCap)
  const capTone = !gated || marketCapNow === undefined || isSettledStrategy(s) ? 'text-muted' : inside ? 'text-positive' : 'text-warning'
  const canEditCap = !limit && !isSettledStrategy(s)
  const target = s.limit?.targetPriceUsd ?? null
  const last = s.limit?.lastPriceUsd ?? null
  const toGo = target !== null && last !== null && last > 0 ? ((target - last) / last) * 100 : null
  const cadence = `every ${formatDuration(s.dca.intervalSeconds)}`
  const next =
    s.status === 'active' && s.nextRunAt
      ? `Next ${formatNextRun(s.nextRunAt)}`
      : s.status === 'active' && pending > 0
        ? 'Filling…'
        : s.status === 'scheduled' && s.nextRunAt
          ? `Starts ${formatNextRun(s.nextRunAt)}`
          : s.status === 'scheduled' && s.isPrivate
            ? 'Waiting for funds'
            : null
  return (
    <tr
      data-strategy-id={s.id}
      className="group cursor-pointer border-t border-line hover:bg-surface-2"
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`${tokenSymbol} ${strategyKind(s)}`}
    >
      {/* the pinned cell paints its own background, so it follows the row's hover by hand; a phone stacks the ticker and the kind
          so the pinned column stays about a third of the screen */}
      <td className={`${PIN} ${edge} w-max px-3 py-3 group-hover:bg-surface-2`}>
        <div className="flex items-center gap-1.5 sm:gap-2.5">
          {/* pause or resume leads the row (John, Nov 2 2026: the button must be easy to reach); a finished row's slot duplicates
              it instead (Nov 3 2026), so running a strategy again is one tap from its row */}
          <span className="flex size-7 shrink-0 items-center justify-center sm:size-8">
            {isSettledStrategy(s) && onDuplicate ? <DuplicateButton onClick={onDuplicate} /> : <PauseResumeButton strategy={s} />}
          </span>
          <TokenIcon symbol={tokenSymbol} imageUrl={tokenLogo(s.token.address, s.token.imageUrl ?? undefined)} />
          {/* the cell is `w-max` and this block is not shrinkable: WebKit sized the column without the ellipsis-cut name and
              the text ran past the pinned cell (John, Nov 2 2026); the name is capped instead */}
          <div>
            <div className="flex flex-col whitespace-nowrap sm:flex-row sm:items-center sm:gap-2">
              <span className="font-semibold">{tokenSymbol}</span>
              <span className="hidden h-3.5 w-px shrink-0 bg-line sm:block" aria-hidden="true" />
              <span className="text-xs font-medium text-muted sm:text-sm sm:text-text">{strategyKind(s)}</span>
            </div>
            {s.name ? (
              <div className="max-w-[5rem] truncate text-xs text-muted sm:max-w-[12rem]" title={s.name}>
                {s.name}
              </div>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-2.5 py-3">
        {/* where the strategy trades, its logo alone (John, Oct 31 2026), then the state. The logo's box is sized by hand
            and never shrinks: WebKit sized this column without an auto-width flex item and laid the logo out at 0px wide,
            so a phone showed the badge alone (John, Nov 3 2026: "the exchange icons are broken or minified") */}
        <span className="flex items-center gap-2">
          <span className="inline-flex size-4 shrink-0" role="img" title={CUSTODY_LABELS[s.platform]} aria-label={CUSTODY_LABELS[s.platform]}>
            <CustodyLogo custody={s.platform} size="size-4" />
          </span>
          <StatusBadge status={s.status} />
        </span>
        {s.lastError ? (
          <div className="mt-1 max-w-[9rem] truncate text-xs text-negative" title={s.lastError}>
            {s.lastError}
          </div>
        ) : null}
      </td>
      <td className="px-2.5 py-3">
        {limit ? (
          <Stacked
            align="left"
            top={s.status === 'completed' ? 'Filled' : s.status === 'active' && last !== null ? `Now ${formatPrice(last)}` : s.status === 'active' ? 'Checking the price…' : '–'}
            bottom={s.status === 'active' && toGo !== null ? `${toGo > 0 ? '+' : ''}${toGo.toFixed(1)}% to go` : undefined}
          />
        ) : (
          <div className="min-w-28">
            <div className="whitespace-nowrap tabular-nums">
              {confirmed} <span className="text-muted">/ {orders}</span>
              <span className="text-xs text-muted"> · {cadence}</span>
            </div>
            {/* the orders as a bar: filled ones solid, the ones in flight lighter; a finished plan's bar fades like its status dot */}
            <div className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={confirmed} aria-valuemin={0} aria-valuemax={orders} aria-label={`${confirmed} of ${orders} orders filled`}>
              <span className={`h-full ${s.status === 'completed' ? 'bg-accent/35' : s.status === 'cancelled' ? 'bg-muted/40' : 'bg-accent'}`} style={{ width: `${(confirmed / orders) * 100}%` }} />
              {pending > 0 ? <span className="h-full bg-accent/40" style={{ width: `${(pending / orders) * 100}%` }} /> : null}
            </div>
            {next ? <div className="mt-1 whitespace-nowrap text-xs tabular-nums">{next}</div> : null}
          </div>
        )}
      </td>
      <td className="px-2.5 py-3">
        {limit ? (
          <Stacked top={`${shortRaw(s.dca.totalAmountRaw, inToken.decimals, inToken.symbol)} ${inToken.symbol}`} bottom={target !== null ? `at ${formatPrice(target)}` : undefined} />
        ) : (
          <Stacked
            top={
              <>
                {shortRaw(done, inToken.decimals, inToken.symbol)} <span className="text-muted">/ {shortRaw(s.dca.totalAmountRaw, inToken.decimals, inToken.symbol)}</span> {inToken.symbol}
              </>
            }
            bottom={done > 0n && s.filledUsd !== null ? formatUsdShort(s.filledUsd) : undefined}
          />
        )}
      </td>
      <td className="px-2.5 py-3">
        {received === null ? (
          <div className="text-right text-muted">–</div>
        ) : (
          <Stacked
            top={
              <span className="inline-flex items-center gap-1.5">
                {received > 0n ? '+' : ''}
                {shortRaw(received, outToken.decimals, outToken.symbol)}
                <TokenIcon symbol={outToken.symbol} imageUrl={outToken.imageUrl} size="size-4" />
                {outToken.symbol}
              </span>
            }
            bottom={s.receivedUsd !== null && received > 0n ? formatUsdShort(s.receivedUsd) : undefined}
          />
        )}
      </td>
      <td className="px-2.5 py-3">
        {limit ? (
          <div className="text-right text-muted">–</div>
        ) : (
          <Stacked
            top={s.perOrderUsd !== null ? `~${formatUsd(s.perOrderUsd)}` : `${shortRaw(s.dca.perOrderAmountRaw, inToken.decimals, inToken.symbol)} ${inToken.symbol}`}
            bottom={
              s.perOrderUsd !== null
                ? `${shortRaw(s.dca.perOrderAmountRaw, inToken.decimals, inToken.symbol)} ${inToken.symbol}${s.execution.randomizeAmount ? ` ±${s.execution.amountVariancePct ?? 40}%` : ''}`
                : s.execution.randomizeAmount
                  ? `±${s.execution.amountVariancePct ?? 40}%`
                  : undefined
            }
          />
        )}
      </td>
      <td className="px-2.5 py-3">
        <Stacked
          top={
            canEditCap ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 whitespace-nowrap hover:text-accent"
                aria-label="Edit the market cap range"
                onClick={(e) => {
                  e.stopPropagation()
                  onEditMarketCap()
                }}
                // Enter and Space stay with the button (the row would open the detail); Escape must still reach the dialog's window listener
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
                }}
              >
                {formatMarketCapRange(minCap, maxCap)}
                <Pencil className="size-3.5 text-muted" aria-hidden="true" />
              </button>
            ) : (
              formatMarketCapRange(minCap, maxCap)
            )
          }
          bottom={marketCapNow !== undefined ? `Now ${formatUsd(marketCapNow, true)}` : undefined}
          tone={capTone}
        />
      </td>
    </tr>
  )
}
